import { defineCatalog } from "@json-render/core";
import { schema } from "@json-render/react/schema";
import { z } from "zod";

/**
 * The json-render catalog. Every component the agent (or any code) can
 * stream into the UI is declared here with its zod-typed props. The agent's
 * tool surface = the components in this catalog + the actions below.
 *
 * Component naming hews to the original Zeemart screens: Card, OutletPill,
 * SupplierRow, OrderRow, KPI, Toolbar, EmptyState, Skeleton.
 */
export const catalog = defineCatalog(schema, {
  components: {
    Page: {
      props: z.object({
        title: z.string(),
        subtitle: z.string().nullable(),
      }),
      slots: ["default"],
      description: "Top-level page container with a title and subtitle.",
    },
    Section: {
      props: z.object({
        title: z.string().nullable(),
        density: z.enum(["tight", "regular", "airy"]).nullable(),
      }),
      slots: ["default"],
      description: "A vertically stacked section of cards or rows.",
    },
    Card: {
      props: z.object({
        title: z.string().nullable(),
        eyebrow: z.string().nullable(),
        tone: z.enum(["plain", "nomni", "danger", "amber"]).nullable(),
      }),
      slots: ["default"],
      description: "Panel surface for grouped content.",
    },
    KPI: {
      props: z.object({
        label: z.string(),
        value: z.string(),
        delta: z.string().nullable(),
        tone: z.enum(["up", "down", "flat"]).nullable(),
      }),
      description: "A headline number with a label and optional delta.",
    },
    OutletPill: {
      props: z.object({
        outletId: z.string(),
        outletName: z.string(),
        status: z.string().nullable(),
        timeZone: z.string().nullable(),
        selected: z.boolean().nullable(),
      }),
      description: "A clickable outlet chip; clicking selects the outlet for the session.",
    },
    SupplierRow: {
      props: z.object({
        supplierId: z.string(),
        supplierName: z.string(),
        deliveryDays: z.string().nullable(),
        minOrder: z.string().nullable(),
      }),
      description: "Row in a supplier list, click to open catalog.",
    },
    OrderRow: {
      props: z.object({
        poId: z.string(),
        supplierName: z.string(),
        total: z.string(),
        status: z.enum(["placed", "pending", "acknowledged", "received", "cancelled"]),
        placedAt: z.string(),
      }),
      description: "Row in a purchase order history list.",
    },
    Toolbar: {
      props: z.object({
        align: z.enum(["start", "between", "end"]).nullable(),
      }),
      slots: ["default"],
      description: "Horizontal bar for actions and filters.",
    },
    Button: {
      props: z.object({
        label: z.string(),
        action: z.string(),
        variant: z.enum(["primary", "ghost", "danger"]).nullable(),
        size: z.enum(["sm", "md", "lg"]).nullable(),
      }),
      description: "Clickable button. The `action` is dispatched to the host.",
    },
    Markdown: {
      props: z.object({
        body: z.string(),
      }),
      description: "Render Markdown text (plain rendering, no HTML).",
    },
    EmptyState: {
      props: z.object({
        title: z.string(),
        body: z.string().nullable(),
      }),
      description: "Used when a result set is empty.",
    },
    Skeleton: {
      props: z.object({
        lines: z.number().nullable(),
      }),
      description: "Shimmer placeholder while data loads.",
    },
  },
  actions: {},
});

/**
 * Action names the host listens for. Components dispatch via NomniDispatch
 * (see render/dispatch.ts). These are intentionally OUTSIDE the json-render
 * action system because we want to carry per-click params (outletId, poId)
 * which the catalog's typed-action system doesn't ergonomically support.
 */
export const ACTIONS = {
  selectOutlet: "selectOutlet",
  openSupplier: "openSupplier",
  openOrder: "openOrder",
  reorder: "reorder",
} as const;

export type Catalog = typeof catalog;
